import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  Switch,
  Modal,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp } from '../../../../types/navigation';
import { gymMembershipInfoService } from '../../../../services/gymMembershipInfo';
import { vaultService } from '../../../../services/vaultService';

import { createLogger } from '../../../../utils/logger';
import { ensureCredentialWriteKey } from '../../../../services/encryption/credentialFields';
const logger = createLogger('GymMembershipInput');

export const GymMembershipInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<RouteProp<Record<string, { gymMembershipId?: string; isEdit?: boolean }>, string>>();
  const { gymMembershipId, isEdit } = route.params || {};
  const [hasOnlinePortal, setHasOnlinePortal] = useState(false);
  const [showMembershipTypeModal, setShowMembershipTypeModal] = useState(false);
  const [showPaymentMethodModal, setShowPaymentMethodModal] = useState(false);
  const [showUsername, setShowUsername] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showAccountNumber, setShowAccountNumber] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showAddAnotherDone, setShowAddAnotherDone] = useState(false);
  const [savedGymMembershipId, setSavedGymMembershipId] = useState<string | undefined>();
  
  const [formData, setFormData] = useState({
    gymName: '',
    membershipType: '',
    monthlyCost: '',
    contractLength: '',
    membershipStartDate: '',
    contractEndDate: '',
    autopayEnabled: false,
    paymentMethod: '',
    membershipId: '',
    accessCode: '',
    branchLocation: '',
    emergencyContact: '',
    emergencyPhone: '',
    website: '',
    notes: '',
    username: '',
    password: '',
    accountNumber: '',
  });

  const membershipTypes = ['Basic', 'Premium', 'Family', 'Student', 'Senior', 'Corporate', 'Other'];
  const paymentMethods = ['Bank Account', 'Credit Card', 'Debit Card', 'Cash', 'Other'];

  // Load existing gym membership data when editing
  useEffect(() => {
    logger.debug('[Edit Debug] useEffect triggered - isEdit:', isEdit, 'gymMembershipId:', gymMembershipId);
    if (isEdit && gymMembershipId) {
      logger.debug('[Edit Debug] Loading gym membership data for editing');
      loadGymMembershipData();
    }
  }, [isEdit, gymMembershipId]);

  const loadGymMembershipData = async () => {
    try {
      logger.debug('[Edit Debug] loadGymMembershipData called with ID:', gymMembershipId);
      if (!gymMembershipId) {
        logger.debug('[Edit Debug] No gymMembershipId provided, returning');
        return;
      }
      
      logger.debug('[Edit Debug] Fetching data for ID:', gymMembershipId);
      const data = await gymMembershipInfoService.getGymMembershipInfoById(gymMembershipId);
      logger.debug('[Edit Debug] Received data:', data);
      
      if (data) {
        setFormData({
          gymName: data.gymName || '',
          membershipType: data.membershipType || '',
          monthlyCost: data.monthlyCost ? formatCurrency(data.monthlyCost.toString()) : '',
          contractLength: data.contractLength || '',
          membershipStartDate: data.membershipStartDate || '',
          contractEndDate: data.contractEndDate || '',
          autopayEnabled: data.autopayEnabled || false,
          paymentMethod: data.paymentMethod || '',
          membershipId: data.membershipId || '',
          accessCode: data.accessCode || '',
          branchLocation: data.branchLocation || '',
          emergencyContact: data.emergencyContact || '',
          emergencyPhone: data.emergencyPhone || '',
          website: data.website || '',
          notes: data.notes || '',
          username: '',
          password: '',
          accountNumber: '',
        });

        // Load portal credentials
        try {
          const portalData = await vaultService.getPortalCredentials(gymMembershipId, 'gym-membership');
          if (portalData) {
            setFormData(prev => ({
              ...prev,
              username: portalData.username || '',
              password: portalData.password || '',
              membershipId: portalData.accountNumber || prev.membershipId,
            }));
            setHasOnlinePortal(true);
          }
        } catch (vaultError) {
          logger.debug('Could not load portal credentials:', vaultError);
        }
      }
    } catch (error) {
      logger.error('[Edit Debug] Error loading gym membership data:', error);
      Alert.alert('Error', 'Failed to load gym membership information');
    }
  };

  // Format currency with commas
  const formatCurrency = (value: string) => {
    // Remove non-numeric characters except decimal
    const numericValue = value.replace(/[^0-9.]/g, '');
    // Split by decimal
    const parts = numericValue.split('.');
    // Format whole number with commas
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    // Limit decimal places to 2
    if (parts[1]) {
      parts[1] = parts[1].substring(0, 2);
    }
    return parts.join('.');
  };

  // Helper: format phone as (xxx) xxx-xxxx while typing
  const formatPhoneNumber = (text: string) => {
    const cleaned = (text || '').replace(/\D/g, '');
    if (cleaned.length >= 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    } else if (cleaned.length >= 6) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length >= 3) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    }
    return cleaned;
  };

  // Helper: format date as dd/mm/yyyy while typing
  const formatDate = (text: string) => {
    const cleaned = (text || '').replace(/\D/g, '');
    if (cleaned.length >= 8) {
      return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}/${cleaned.slice(4, 8)}`;
    } else if (cleaned.length >= 4) {
      return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}/${cleaned.slice(4)}`;
    } else if (cleaned.length >= 2) {
      return `${cleaned.slice(0, 2)}/${cleaned.slice(2)}`;
    }
    return cleaned;
  };

  const handleCurrencyChange = (text: string) => {
    const cleaned = text.replace(/^\$/, '');
    setFormData({ ...formData, monthlyCost: formatCurrency(cleaned) });
  };

  const handlePhoneChange = (field: string, text: string) => {
    const formatted = formatPhoneNumber(text);
    setFormData({ ...formData, [field]: formatted });
  };

  const handleDateChange = (field: string, text: string) => {
    const formatted = formatDate(text);
    setFormData({ ...formData, [field]: formatted });
  };

  const handleMembershipTypeSelect = (type: string) => {
    setFormData({ ...formData, membershipType: type });
    setShowMembershipTypeModal(false);
  };

  const handlePaymentMethodSelect = (method: string) => {
    setFormData({ ...formData, paymentMethod: method });
    setShowPaymentMethodModal(false);
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      // K5: a login is saved only under a Recovery Code; ask before anything is written.
      if ((formData.username || formData.password || formData.membershipId) && !(await ensureCredentialWriteKey())) {
        setSubmitting(false);
        return;
      }
      
      // Basic validation
      if (!formData.gymName) {
        Alert.alert('Validation Error', 'Gym name is required');
        setSubmitting(false);
        return;
      }

      // Prepare payload for API
      const payload: any = {
        gymName: formData.gymName,
        membershipType: formData.membershipType || undefined,
        monthlyCost: formData.monthlyCost ? parseFloat(formData.monthlyCost.replace(/[^0-9.]/g, '')) : undefined,
        contractLength: formData.contractLength || undefined,
        membershipStartDate: formData.membershipStartDate || undefined,
        contractEndDate: formData.contractEndDate || undefined,
        autopayEnabled: formData.autopayEnabled,
        paymentMethod: formData.paymentMethod || undefined,
        membershipId: formData.membershipId || undefined,
        accessCode: formData.accessCode || undefined,
        branchLocation: formData.branchLocation || undefined,
        emergencyContact: formData.emergencyContact || undefined,
        emergencyPhone: formData.emergencyPhone.replace(/\D/g, '') || undefined,
        website: formData.website || undefined,
        notes: formData.notes || undefined,
      };

      let saved;
      let savedId;
      if (isEdit && gymMembershipId) {
        saved = await gymMembershipInfoService.updateGymMembershipInfo(gymMembershipId, payload);
        // Handle wrapped response format { success, data, message }
        const updatedData = saved?.data || saved;
        savedId = updatedData.id || gymMembershipId;
      } else {
        saved = await gymMembershipInfoService.createGymMembershipInfo(payload);
        // Handle wrapped response format { success, data, message }
        const createdData = saved?.data || saved;
        savedId = createdData.id;
      }
      
      // Store the saved ID for the Done button
      setSavedGymMembershipId(savedId);

      // Store portal credentials in Vault if provided
      if (savedId && (formData.username || formData.password || formData.membershipId)) {
        try {
          await vaultService.storePortalCredentials(savedId, 'gym-membership', {
            username: formData.username,
            password: formData.password,
            accountNumber: formData.membershipId,
          });
        } catch (e) {
          logger.error('Vault write failed for gym membership portal credentials:', e);
        }
      }

      if (isEdit) {
        // For edit mode, navigate to lock/unlock screen
        Alert.alert('Success', 'Gym membership information updated successfully!', [
          {
            text: 'OK',
            onPress: () => navigation.navigate('GymMembershipLockUnlock', { gymMembershipId: gymMembershipId })
          }
        ]);
      } else {
        // Clear the form and reset states for Add Another
        setFormData({
          gymName: '',
          membershipType: '',
          monthlyCost: '',
          contractLength: '',
          membershipStartDate: '',
          contractEndDate: '',
          autopayEnabled: false,
          paymentMethod: '',
          membershipId: '',
          accessCode: '',
          branchLocation: '',
          emergencyContact: '',
          emergencyPhone: '',
          website: '',
          notes: '',
          username: '',
          password: '',
          accountNumber: '',
        });
        setHasOnlinePortal(false);
        
        // Show Add Another / Done buttons after successful save
        setShowAddAnotherDone(true);
        // Reset submitting so buttons are interactive
        setSubmitting(false);
      }
    } catch (error: any) {
      logger.error('Error saving gym membership info:', error);
      Alert.alert('Error', 'Failed to save gym membership information. Please try again.');
      setSubmitting(false);
    }
  };

  const handleAddAnother = () => {
    // Clear form and hide buttons
    setFormData({
      gymName: '',
      membershipType: '',
      monthlyCost: '',
      contractLength: '',
      membershipStartDate: '',
      contractEndDate: '',
      autopayEnabled: false,
      paymentMethod: '',
      membershipId: '',
      accessCode: '',
      branchLocation: '',
      emergencyContact: '',
      emergencyPhone: '',
      website: '',
      notes: '',
      username: '',
      password: '',
      accountNumber: '',
    });
    setHasOnlinePortal(false);
    setShowAddAnotherDone(false);
    setSubmitting(false);
  };

  const handleDone = () => {
    setSubmitting(false);
    // Navigate to lock/unlock with the saved ID
    logger.debug('[Done Debug] Using ID:', savedGymMembershipId);
    if (savedGymMembershipId) {
      (navigation as any).navigate('GymMembershipLockUnlock', { gymMembershipId: savedGymMembershipId });
    } else {
      Alert.alert('Error', 'No gym membership ID found. Please try again.');
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>Gym Membership Info</Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollViewContent} keyboardShouldPersistTaps="handled" automaticallyAdjustKeyboardInsets={true}>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Gym Name *</Text>
            <TextInput
              style={styles.input}
              value={formData.gymName}
              onChangeText={(text) => setFormData({ ...formData, gymName: text })}
              placeholder="Enter gym name"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Membership Type</Text>
            <TouchableOpacity
              style={[styles.input, styles.dropdownButton]}
              onPress={() => setShowMembershipTypeModal(true)}
            >
              <Text style={[styles.inputText, !formData.membershipType && styles.placeholderText]}>
                {formData.membershipType || 'Select membership type'}
              </Text>
              <Image source={require('../../../../assets/icons/down.png')} style={styles.dropdownIcon} />
            </TouchableOpacity>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Monthly Cost</Text>
            <TextInput
              style={styles.input}
              value={formData.monthlyCost ? `$${formData.monthlyCost}` : ''}
              onChangeText={handleCurrencyChange}
              placeholder="Ex: $25.00"
              placeholderTextColor="#A9A9A9"
              keyboardType="decimal-pad"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Membership Start Date</Text>
            <TextInput
              style={styles.input}
              value={formData.membershipStartDate}
              onChangeText={(text) => handleDateChange('membershipStartDate', text)}
              placeholder="DD/MM/YYYY"
              placeholderTextColor="#A9A9A9"
              keyboardType="numeric"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Contract Length</Text>
            <TextInput
              style={styles.input}
              value={formData.contractLength}
              onChangeText={(text) => setFormData({ ...formData, contractLength: text })}
              placeholder="e.g., 12 months, No contract"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Contract End Date</Text>
            <TextInput
              style={styles.input}
              value={formData.contractEndDate}
              onChangeText={(text) => handleDateChange('contractEndDate', text)}
              placeholder="DD/MM/YYYY"
              placeholderTextColor="#A9A9A9"
              keyboardType="numeric"
            />
          </View>

          <View style={styles.toggleContainer}>
            <Text style={styles.toggleText}>Autopay Enabled</Text>
            <Switch
              trackColor={{ false: "#767577", true: "#29BFC0" }}
              thumbColor={formData.autopayEnabled ? "#fff" : "#f4f3f4"}
              ios_backgroundColor="#767577"
              onValueChange={(value) => setFormData({ ...formData, autopayEnabled: value })}
              value={formData.autopayEnabled}
            />
          </View>

          {formData.autopayEnabled && (
            <View style={styles.inputGroup}>
              <Text style={styles.label}>Payment Method</Text>
              <TouchableOpacity
                style={[styles.input, styles.dropdownButton]}
                onPress={() => setShowPaymentMethodModal(true)}
              >
                <Text style={[styles.inputText, !formData.paymentMethod && styles.placeholderText]}>
                  {formData.paymentMethod || 'Select payment method'}
                </Text>
                <Image
                  source={require('../../../../assets/icons/down.png')}
                  style={styles.dropdownIcon}
                />
              </TouchableOpacity>
            </View>
          )}

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Membership ID</Text>
            <TextInput
              style={styles.input}
              value={formData.membershipId}
              onChangeText={(text) => setFormData({ ...formData, membershipId: text })}
              placeholder="Enter membership ID"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Access Code</Text>
            <TextInput
              style={styles.input}
              value={formData.accessCode}
              onChangeText={(text) => setFormData({ ...formData, accessCode: text })}
              placeholder="Enter access code"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Branch Location</Text>
            <TextInput
              style={styles.input}
              value={formData.branchLocation}
              onChangeText={(text) => setFormData({ ...formData, branchLocation: text })}
              placeholder="Enter branch location"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Emergency Contact</Text>
            <TextInput
              style={styles.input}
              value={formData.emergencyContact}
              onChangeText={(text) => setFormData({ ...formData, emergencyContact: text })}
              placeholder="Enter emergency contact name"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Emergency Phone</Text>
            <TextInput
              style={styles.input}
              value={formData.emergencyPhone}
              onChangeText={(text) => handlePhoneChange('emergencyPhone', text)}
              placeholder="(555) 555-5555"
              placeholderTextColor="#A9A9A9"
              keyboardType="phone-pad"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Website</Text>
            <TextInput
              style={styles.input}
              value={formData.website}
              onChangeText={(text) => setFormData({ ...formData, website: text })}
              placeholder="Ex: www.planetfitness.com"
              placeholderTextColor="#A9A9A9"
              keyboardType="url"
              autoCapitalize="none"
            />
          </View>

          {/* Portal Toggle - Moved after website */}
          <View style={styles.inputGroup}>
            <TouchableOpacity
              style={styles.toggleContainer}
              onPress={() => setHasOnlinePortal(!hasOnlinePortal)}
            >
              <View style={[styles.toggle, hasOnlinePortal && styles.toggleActive]}>
                <View style={[styles.toggleThumb, hasOnlinePortal && styles.toggleThumbActive]} />
              </View>
              <Text style={styles.toggleLabel}>Does your gym have an online portal?</Text>
            </TouchableOpacity>
            <Text style={styles.helperText}>
              If yes, enter your login credentials. These will be securely stored in your vault and will only be shared with your successor after your End of Life event.
            </Text>
          </View>

          {hasOnlinePortal && (
            <>
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Portal Username 🔒</Text>
                <View style={styles.passwordContainer}>
                  <TextInput
                    style={[styles.input, styles.passwordInput]}
                    value={formData.username}
                    onChangeText={(text) => setFormData({ ...formData, username: text })}
                    placeholder="Enter Username"
                    placeholderTextColor="#A9A9A9"
                    secureTextEntry={!showUsername}
                    autoCapitalize="none"
                  />
                  <TouchableOpacity
                    style={styles.eyeButton}
                    onPress={() => setShowUsername(!showUsername)}
                  >
                    <Image
                      source={showUsername ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                      style={styles.eyeIcon}
                    />
                  </TouchableOpacity>
                </View>
                <Text style={styles.vaultText}>Stored securely - shared only after EOL</Text>
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Portal Password 🔒</Text>
                <View style={styles.passwordContainer}>
                  <TextInput
                    style={[styles.input, styles.passwordInput]}
                    value={formData.password}
                    onChangeText={(text) => setFormData({ ...formData, password: text })}
                    placeholder="Enter Password"
                    placeholderTextColor="#A9A9A9"
                    secureTextEntry={!showPassword}
                  />
                  <TouchableOpacity
                    style={styles.eyeButton}
                    onPress={() => setShowPassword(!showPassword)}
                  >
                    <Image
                      source={showPassword ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                      style={styles.eyeIcon}
                    />
                  </TouchableOpacity>
                </View>
                <Text style={styles.vaultText}>Stored securely - shared only after EOL</Text>
              </View>
            </>
          )}

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Notes</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.notes}
              onChangeText={(text) => setFormData({ ...formData, notes: text })}
              placeholder="Additional information..."
              placeholderTextColor="#A9A9A9"
              multiline
              numberOfLines={4}
            />
          </View>

          {showAddAnotherDone ? (
            <View style={styles.addAnotherDoneContainer}>
              <TouchableOpacity
                style={styles.addAnotherButton}
                onPress={handleAddAnother}
              >
                <Text style={styles.submitButtonText}>Add Another</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.doneButton}
                onPress={handleDone}
              >
                <Text style={styles.submitButtonText}>Done</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <View style={styles.submitButtonContainer}>
              {submitting ? (
                <TouchableOpacity style={[styles.submitButton, styles.buttonDisabled]} disabled>
                  <ActivityIndicator color="#FFFFFF" />
                </TouchableOpacity>
              ) : (
                <TouchableOpacity
                  style={styles.submitButton}
                  onPress={handleSubmit}
                >
                  <Text style={styles.submitButtonText}>{isEdit ? 'Update' : 'Submit'}</Text>
                </TouchableOpacity>
              )}
            </View>
          )}
        </ScrollView>
      </View>

      {/* Membership Type Modal */}
      <Modal
        visible={showMembershipTypeModal}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setShowMembershipTypeModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Membership Type</Text>
              <TouchableOpacity
                onPress={() => setShowMembershipTypeModal(false)}
                style={styles.modalCloseButton}
              >
                <Text style={styles.modalCloseText}>×</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.stateList}>
              {membershipTypes.map((type) => (
                <TouchableOpacity
                  key={type}
                  style={styles.stateItem}
                  onPress={() => handleMembershipTypeSelect(type)}
                >
                  <Text style={styles.stateItemText}>{type}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* Payment Method Modal */}
      <Modal
        visible={showPaymentMethodModal}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setShowPaymentMethodModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Payment Method</Text>
              <TouchableOpacity
                onPress={() => setShowPaymentMethodModal(false)}
                style={styles.modalCloseButton}
              >
                <Text style={styles.modalCloseText}>×</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.stateList}>
              {paymentMethods.map((method) => (
                <TouchableOpacity
                  key={method}
                  style={styles.stateItem}
                  onPress={() => handlePaymentMethodSelect(method)}
                >
                  <Text style={styles.stateItemText}>{method}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </View>
      </Modal>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
    paddingHorizontal: ms(30),
    textAlign: 'center',
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
    paddingBottom: ms(120),
  },
  scrollViewContent: {
    paddingBottom: ms(350),
  },
  inputGroup: {
    marginBottom: ms(16),
  },
  label: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#6B7280',
    marginBottom: ms(8),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  textArea: {
    height: s(100),
    textAlignVertical: 'top',
  },
  dropdownButton: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  inputText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  placeholderText: {
    color: '#A9A9A9',
  },
  dropdownIcon: {
    width: s(20),
    height: s(20),
  },
  vaultText: {
    fontSize: ms(14, 0.3),
    color: '#FF9F5A',
    marginTop: ms(5),
    fontWeight: '600',
  },
  toggleText: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#6B7280',
    marginRight: ms(16),
  },
  toggleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: ms(10),
  },
  toggle: {
    width: s(50),
    height: s(30),
    borderRadius: ms(15),
    backgroundColor: '#E5E7EB',
    marginRight: ms(15),
    padding: ms(2),
    justifyContent: 'center',
  },
  toggleActive: {
    backgroundColor: '#29BFC0',
  },
  toggleThumb: {
    width: s(26),
    height: s(26),
    borderRadius: ms(13),
    backgroundColor: '#FFFFFF',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 2,
  },
  toggleThumbActive: {
    alignSelf: 'flex-end',
  },
  toggleLabel: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    flex: 1,
  },
  helperText: {
    fontSize: ms(14, 0.3),
    color: '#FF9F5A',
    marginTop: ms(5),
    lineHeight: ms(18, 0.3),
  },
  passwordContainer: {
    position: 'relative',
  },
  passwordInput: {
    paddingRight: ms(50),
  },
  eyeButton: {
    position: 'absolute',
    right: ms(15),
    top: ms(15),
    padding: ms(5),
  },
  eyeIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#6B7280',
  },
  addAnotherDoneContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(120),
    gap: ms(20),
  },
  submitButtonContainer: {
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(40),
  },
  submitButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(60),
    alignItems: 'center',
    marginBottom: ms(120),
    minWidth: s(200),
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  addAnotherButton: {
    backgroundColor: '#FF9F5A',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
    alignItems: 'center',
    minWidth: s(140),
  },
  doneButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
    alignItems: 'center',
    minWidth: s(140),
  },
  buttonDisabled: {
    opacity: 0.7,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    maxHeight: '80%',
    width: '100%',
    paddingBottom: ms(20),
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: ms(16),
    borderBottomWidth: ms(1),
    borderBottomColor: '#d8dbdd',
  },
  modalTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  modalCloseButton: {
    padding: ms(8),
  },
  modalCloseText: {
    fontSize: ms(20, 0.3),
    color: '#1C2C4F',
  },
  stateList: {
    padding: ms(16),
  },
  stateItem: {
    paddingVertical: ms(12),
    borderBottomWidth: ms(1),
    borderBottomColor: '#d8dbdd',
  },
  stateItemText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
});